import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, Ship, AlertTriangle, Receipt } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../../components/ui/SectionCard';
import { BarChart } from '../../components/ui/BarChart';
import api from '../../services/api';
import { SHIPMENT_STATUS, INVOICE_STATUS, fmtDate, fmtAmount, groupByMonth } from '../../utils/dashboardUtils';
import { DashHeader, DashError, StatusBreakdown, MiniEmpty } from './DashboardShared';

const OVERDUE_COLS = [
  {
    key: 'reference',
    label: 'N° Facture',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'shipToName',
    label: 'Client',
    render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
  },
  {
    key: 'dueDate',
    label: 'Échéance',
    render: (v) => <span className="text-slate-600 text-[12px]">{v}</span>,
  },
  {
    key: 'ageDays',
    label: 'Retard',
    align: 'right',
    render: (v) =>
      v > 0
        ? <span className="tabular-nums font-semibold text-[12px] text-rose-600">+{v} j</span>
        : <span className="text-slate-400">—</span>,
  },
  {
    key: 'amountDisplay',
    label: 'Montant',
    align: 'right',
    render: (v) => <span className="tabular-nums font-semibold text-slate-800 text-[12.5px]">{v}</span>,
  },
];

const INCIDENT_COLS = [
  {
    key: 'containerRef',
    label: 'Réf.',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'productName',
    label: 'Produit',
    render: (v) => <span className="font-medium text-slate-700">{v}</span>,
  },
  {
    key: 'departure',
    label: 'Route',
    render: (_, row) => (
      <span className="text-slate-500 text-[12px]">{row.departure} → {row.arrival}</span>
    ),
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
];

export default function DirectionDashboard() {
  const { t, i18n } = useTranslation();
  const timestampColumns = syncTimestampColumns(t, i18n.language);
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [invoices, setInvoices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([api.get('/shipments'), api.get('/orders'), api.get('/invoices?limit=1000')])
      .then(([sRes, oRes, iRes]) => {
        setShipments(Array.isArray(sRes.data) ? sRes.data : (sRes.data?.data ?? []));
        setOrders(oRes.data);
        setInvoices(iRes.data.rows ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const enrichedShipments = useMemo(() => {
    const orderMap = {};
    orders.forEach((o) => { orderMap[o._id] = o; });
    return shipments.map((s) => {
      const order = orderMap[s.orderId] || {};
      const mapped = SHIPMENT_STATUS[s.status] || { variant: 'info', label: s.status || '—', group: 'other' };
      return {
        ...s,
        statusVariant: mapped.variant,
        statusLabel:   mapped.label,
        statusGroup:   mapped.group,
        productName:   order.productName || '—',
        departure:     order.origin?.city || order.origin?.portCode || '—',
        arrival:       order.destination?.finalDestinationCode || order.destination?.portCode || '—',
      };
    });
  }, [shipments, orders]);

  const enrichedInvoices = useMemo(() => invoices.map((inv) => {
    const mapped = INVOICE_STATUS[inv.status] || { variant: 'info', label: inv.status || '—', group: 'other' };
    return {
      ...inv,
      statusVariant: mapped.variant,
      statusLabel:   mapped.label,
      statusGroup:   mapped.group,
      issuedAt:      fmtDate(inv.issuedAt),
      dueDate:       fmtDate(inv.dueDate),
      amountDisplay: fmtAmount(inv.amountTTC ?? inv.amountEur, inv.fretCurrencyCode || 'EUR'),
      ageDays:       inv.ageDays || 0,
      shipToName:    inv.shipToName || inv.billToCustomerNo || '—',
    };
  }), [invoices]);

  const kpis = useMemo(() => {
    const currency = invoices[0]?.fretCurrencyCode || 'EUR';
    const sumAll   = invoices.reduce((s, inv) => s + (inv.amountTTC ?? inv.amountEur ?? 0), 0);
    const pending  = enrichedInvoices.filter((r) => r.statusGroup === 'pending');
    const pendingAmt = pending.reduce((s, r) => s + (r.amountTTC ?? r.amountEur ?? 0), 0);
    return {
      totalAmount:   sumAll,
      pendingAmount: pendingAmt,
      inTransit:     enrichedShipments.filter((s) => s.statusGroup === 'transit').length,
      incidents:     enrichedShipments.filter((s) => s.statusGroup === 'incident').length,
      currency,
    };
  }, [invoices, enrichedInvoices, enrichedShipments]);

  const shipmentMonthly = useMemo(() => groupByMonth(shipments, 'createdAt'), [shipments]);

  const invoiceStatusItems = [
    { label: 'En attente', count: enrichedInvoices.filter((r) => r.statusGroup === 'pending').length,  variant: 'pending'   },
    { label: 'Payées',     count: enrichedInvoices.filter((r) => r.statusGroup === 'paid').length,     variant: 'paid'      },
    { label: 'En retard',  count: enrichedInvoices.filter((r) => r.statusGroup === 'overdue').length,  variant: 'overdue30' },
  ];

  const overdueRows  = useMemo(() => enrichedInvoices.filter((r) => r.statusGroup === 'overdue').slice(0, 5), [enrichedInvoices]);
  const incidentRows = useMemo(() => enrichedShipments.filter((s) => s.statusGroup === 'incident').slice(0, 5), [enrichedShipments]);

  return (
    <div className="flex flex-col gap-5">
      <DashHeader
        title="Tableau de bord — Direction"
        sub={`Bonjour ${user?.firstName} — vue stratégique`}
        onRefresh={load}
        loading={loading}
      />

      {error && <DashError message={error} onRetry={load} />}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Chiffre d'affaires"
          value={loading ? '—' : fmtAmount(kpis.totalAmount, kpis.currency)}
          icon={TrendingUp}
          sub={!loading ? `${invoices.length} facture${invoices.length !== 1 ? 's' : ''}` : undefined}
        />
        <KpiCard
          label="Montant en attente"
          value={loading ? '—' : fmtAmount(kpis.pendingAmount, kpis.currency)}
          icon={Receipt}
        />
        <KpiCard
          label="Expéditions actives"
          value={loading ? '—' : kpis.inTransit}
          icon={Ship}
          sub={!loading ? `${shipments.length} au total` : undefined}
        />
        <KpiCard
          label="Incidents en cours"
          value={loading ? '—' : kpis.incidents}
          icon={AlertTriangle}
          trend={kpis.incidents > 0 ? 'down' : undefined}
          trendLabel={kpis.incidents > 0 ? `${kpis.incidents} actif${kpis.incidents > 1 ? 's' : ''}` : 'Aucun incident'}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Expéditions par mois" className="col-span-2">
          {!loading && <BarChart data={shipmentMonthly} />}
          {loading && <div className="h-[68px] bg-slate-100 rounded animate-pulse" />}
        </SectionCard>
        <SectionCard title="Facturation — statuts">
          {loading
            ? <div className="space-y-2.5">{[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
            : <StatusBreakdown items={invoiceStatusItems} total={enrichedInvoices.length} />
          }
        </SectionCard>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Retards de paiement"
          action={
            overdueRows.length > 0 && (
              <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                {overdueRows.length}
              </span>
            )
          }
        >
          {overdueRows.length > 0
            ? <Table columns={[...OVERDUE_COLS, ...timestampColumns]} rows={overdueRows} rowKey="_id" />
            : <MiniEmpty label="Aucun retard de paiement" ok />
          }
        </SectionCard>

        <SectionCard
          title="Incidents en cours"
          action={
            incidentRows.length > 0 && (
              <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                {incidentRows.length}
              </span>
            )
          }
        >
          {incidentRows.length > 0
            ? <Table columns={[...INCIDENT_COLS, ...timestampColumns]} rows={incidentRows} rowKey="_id" />
            : <MiniEmpty label="Aucun incident en cours" ok />
          }
        </SectionCard>
      </div>
    </div>
  );
}
